import { PopoverClose } from '@radix-ui/react-popover';
import { ColumnDef } from '@tanstack/react-table';
import { isNil } from 'lodash';
import React, { useMemo } from 'react';
import { LuEllipsisVertical, LuSquarePen, LuTrash } from 'react-icons/lu';

import { CashFlowConfig, CashFlowType, FrequencyType } from '@ndvr/portfolio-api';

import { ClientDataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatCurrency, formatDate } from 'utils';

import { frequencyTypeToLabel } from '../../utils/frequencyToLabel';

interface CashflowTableProps {
    accounts: {
        id: string;
        name: string;
    }[];
    streams: CashFlowConfig[];
    showType?: boolean;
    showPastCashFlows?: boolean;
    onEdit: (stream: CashFlowConfig) => void;
    onDelete?: (stream: CashFlowConfig) => void;
    isLoading: boolean;
}

export const CashflowTable: React.FC<CashflowTableProps> = ({
    accounts,
    streams,
    showType = true,
    isLoading = false,
    showPastCashFlows = false,
    onEdit,
    onDelete,
}) => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    const filteredStreams = useMemo(() => {
        // always show recurring cash flows without an endDate (includes the ones with occurrenceCount set)
        const isOpenEnded = (stream: CashFlowConfig) =>
            stream?.factorDateFrame.frequencyType !== FrequencyType.ONE_TIME && isNil(stream?.factorDateFrame.endDate);

        const isInPast = (stream: CashFlowConfig) => {
            // Use endDate if available, otherwise fallback to startDate
            const relevantDate = stream?.factorDateFrame.endDate || stream?.factorDateFrame.startDate;
            if (!relevantDate) return false; // Treat undefined as not in past
            return relevantDate < today;
        };

        return (
            streams?.filter((stream) => (showPastCashFlows ? stream : isOpenEnded(stream) || !isInPast(stream))) || []
        );
    }, [streams, showPastCashFlows, today]);

    const columns: ColumnDef<CashFlowConfig, any>[] = useMemo(() => {
        const baseColumns: ColumnDef<CashFlowConfig, any>[] = [
            {
                id: 'name',
                header: 'Name',
                accessorFn: (row: CashFlowConfig) => row.title.plainText,
                cell: ({ getValue }) => <span>{getValue() as string}</span>,
                enableSorting: true,
            },
        ];

        if (showType) {
            baseColumns.push({
                id: 'type',
                header: 'Type',
                accessorFn: (row: CashFlowConfig) =>
                    row.cashFlowType === CashFlowType.CONTRIBUTION ? 'Contribution' : 'Withdrawal',
                cell: ({ getValue }) => <span>{getValue() as string}</span>,
                enableSorting: true,
            });
        }

        baseColumns.push(
            {
                id: 'amount',
                header: 'Amount',
                accessorFn: (row: CashFlowConfig) => row.amount,
                cell: ({ row }) => (
                    <span>
                        {formatCurrency(row.original.amount)}
                        {row.original.inflationConfig.inflationAdjusted && (
                            <span className="ml-1 text-xs text-gray-500">
                                (+{row.original.inflationConfig.inflationPercent}%)
                            </span>
                        )}
                    </span>
                ),
                enableSorting: true,
            },
            {
                id: 'frequency',
                header: 'Frequency',
                accessorFn: (row: CashFlowConfig) => frequencyTypeToLabel(row.factorDateFrame.frequencyType),
                cell: ({ getValue }) => <span className="capitalize">{getValue() as string}</span>,
                enableSorting: true,
            },
            {
                id: 'duration',
                header: 'Duration',
                accessorFn: (row: CashFlowConfig) => row.factorDateFrame.startDate,
                cell: ({ row }) => (
                    <span>
                        {row.original.factorDateFrame.frequencyType === FrequencyType.ONE_TIME ||
                        !row.original.factorDateFrame.endDate
                            ? formatDate(row.original.factorDateFrame.startDate)
                            : `${formatDate(row.original.factorDateFrame.startDate)} to ${formatDate(row.original.factorDateFrame.endDate)}`}

                        {row.original.factorDateFrame.frequencyType !== FrequencyType.ONE_TIME &&
                            !row.original.factorDateFrame.endDate &&
                            !row.original.factorDateFrame.occurrenceCount &&
                            ' until cancellation'}

                        {row.original.factorDateFrame.occurrenceCount && (
                            <span className="ml-1 text-xs text-gray-500">
                                ({row.original.factorDateFrame.occurrenceCount} occurrences)
                            </span>
                        )}
                    </span>
                ),
                enableSorting: true,
            },
            {
                id: 'account',
                header: 'Account',
                accessorFn: (row: CashFlowConfig) =>
                    accounts.find((account) => account.id === row.accountGUID)?.name || 'Portfolio Level',
                cell: ({ getValue }) => <span>{getValue() as string}</span>,
                enableSorting: true,
            },
            {
                id: 'actions',
                enableResizing: false,
                header: '',
                cell: ({ row }) => (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100">
                                <LuEllipsisVertical className="h-4 w-4 text-black" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-32 p-0">
                            <div className="py-1">
                                {onEdit && (
                                    <PopoverClose asChild>
                                        <button
                                            onClick={() => onEdit(row.original)}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                                        >
                                            <LuSquarePen className="h-4 w-4" />
                                            Edit
                                        </button>
                                    </PopoverClose>
                                )}
                                {onDelete && (
                                    <PopoverClose asChild>
                                        <button
                                            onClick={() => onDelete(row.original)}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                                        >
                                            <LuTrash className="h-4 w-4" />
                                            Delete
                                        </button>
                                    </PopoverClose>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>
                ),
                enableSorting: false,
            },
        );

        return baseColumns;
    }, [accounts, showType, onEdit, onDelete]);

    return (
        <ClientDataTable
            data={filteredStreams}
            columns={columns}
            isLoading={isLoading}
            loadingRowCount={3}
            emptyMessage="No cashflow found"
        />
    );
};
